import {
  getFormsCountStats,
  normalizeFormsListResponse,
  shouldCountFormInStats,
  withResolvedDemoFields,
} from "./formTabs";

const pickFirstDefined = (...values) => {
  const found = values.find((value) => value != null && value !== "");
  return found;
};

export const unwrapDashboardStats = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  const data =
    payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload;

  const nested =
    data.stats && typeof data.stats === "object" && !Array.isArray(data.stats)
      ? data.stats
      : {};

  return { ...payload, ...data, ...nested };
};

export const pickDashboardStat = (stats, keys, fallback = 0) => {
  for (const key of keys) {
    if (stats?.[key] != null && stats[key] !== "") return stats[key];
  }
  return fallback;
};

export const formatDashboardRate = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0.0";
  return number.toFixed(1);
};

export const getDashboardStatsView = (payload) => {
  if (
    payload &&
    typeof payload === "object" &&
    payload.responseRateLabel != null &&
    "totalForms" in payload
  ) {
    return payload;
  }

  const stats = unwrapDashboardStats(payload);
  const responseRate = pickDashboardStat(stats, [
    "response_ratio",
    "response_rate",
    "responseRate",
  ]);

  return {
    totalUsers: pickDashboardStat(stats, ["total_users", "users", "totalUsers"]),
    totalInterpreters: pickDashboardStat(stats, [
      "total_interpreters",
      "interpreters",
    ]),
    totalForms: pickDashboardStat(stats, ["total_forms", "forms"]),
    totalCampaigns: pickDashboardStat(stats, ["total_campaigns", "campaigns"]),
    totalOrganizations: pickDashboardStat(stats, [
      "total_organizations",
      "total_organisations",
      "organizations",
    ]),
    untranslatedVideos: pickDashboardStat(stats, [
      "untranslated_videos",
      "videos_unassigned",
      "unassigned",
      "unAssigned",
    ]),
    activeForms: pickDashboardStat(stats, ["active_forms", "activeForms"]),
    responseRate,
    responseRateLabel: `${formatDashboardRate(responseRate)}%`,
    completeForms: pickDashboardStat(stats, [
      "complete_forms",
      "completed_forms",
      "completedForms",
    ]),
    totalResponses: pickDashboardStat(stats, [
      "total_responses",
      "totalResponses",
    ]),
    videoResponses: pickDashboardStat(stats, [
      "video_response",
      "video_responses",
      "total_video_responses",
    ]),
    textResponses: pickDashboardStat(stats, [
      "text_response",
      "text_responses",
      "total_text_responses",
    ]),
    videosUnassigned: pickDashboardStat(stats, [
      "videos_unassigned",
      "untranslated_videos",
      "unassigned",
      "unAssigned",
    ]),
    assignedAssignments: pickDashboardStat(stats, [
      "assigned_assignments",
      "assigned",
      "videos_being_translated",
    ]),
    secondCheckAssignment: pickDashboardStat(stats, [
      "second_check_assignment",
      "second_check_assignments",
      "second_check",
    ]),
    activeCampaigns: pickDashboardStat(stats, [
      "active_campaigns",
      "activeCampaigns",
    ]),
    totalParticipants: pickDashboardStat(stats, [
      "total_participants",
      "participants",
    ]),
    totalAssignments: pickDashboardStat(stats, [
      "total_assignments",
      "assignments",
    ]),
    completedAssignments: pickDashboardStat(stats, [
      "completed_assignments",
      "completedAssignments",
    ]),
    incompleteAssignments: pickDashboardStat(stats, [
      "incomplete_assignments",
      "incompleteAssignments",
    ]),
    overdueAssignments: pickDashboardStat(stats, [
      "overdue_assignments",
      "overdueAssignments",
      "overdue",
    ]),
    raw: stats,
  };
};

export const buildDashboardQueryParams = ({
  selectedPeriod,
  dateParams,
  dateRange,
  selectedOrgs = [],
  selectedCampaigns = [],
  selectedForms = [],
  selectedCampaign,
  selectedForm,
}) => {
  const hasCustomRange = Boolean(dateRange?.[0] && dateRange?.[1]);
  const params = {
    start_date:
      selectedPeriod === "lifetime" && !hasCustomRange
        ? null
        : dateParams?.startDate,
    end_date: dateParams?.endDate,
  };

  selectedOrgs.forEach((orgId, index) => {
    params[`organization[${index}]`] = orgId;
  });

  const campaigns = selectedCampaigns.length
    ? selectedCampaigns
    : selectedCampaign
      ? [selectedCampaign]
      : [];
  campaigns.forEach((id, index) => {
    params[`campaign[${index}]`] = id;
  });

  const forms = selectedForms.length
    ? selectedForms
    : selectedForm
      ? [selectedForm]
      : [];
  forms.forEach((id, index) => {
    params[`form[${index}]`] = id;
  });

  return params;
};

export const mapYearlyDashboardData = (yearlyStatsData) => {
  const candidates = [
    yearlyStatsData?.data?.data,
    yearlyStatsData?.data,
    yearlyStatsData,
  ].filter(
    (value) => value && typeof value === "object" && !Array.isArray(value),
  );

  for (const source of candidates) {
    const years = Object.keys(source).filter((key) => /^\d{4}$/.test(key));
    if (!years.length) continue;

    return years
      .map((year) => {
        const entry = source[year] || {};
        return {
          year: parseInt(year, 10),
          text: pickFirstDefined(
            entry.text,
            entry.text_response,
            entry.textResponses,
            0,
          ),
          video: pickFirstDefined(
            entry.video,
            entry.video_response,
            entry.videoResponses,
            0,
          ),
          total: pickFirstDefined(
            entry.total,
            entry.total_responses,
            entry.totalResponses,
            0,
          ),
        };
      })
      .sort((left, right) => left.year - right.year);
  }

  return [];
};

export const getFormsPageStats = (
  formsData,
  isOrganizationContext = false,
) => {
  const stats = getFormsCountStats(
    normalizeFormsListResponse(formsData, isOrganizationContext)
      .map(withResolvedDemoFields)
      .filter(shouldCountFormInStats),
  );

  return {
    totalForms: stats.total,
    activeForms: stats.active,
    completeForms: stats.complete,
    totalResponses: stats.totalResponses,
    responseRate: stats.responseRate,
  };
};

export const getCampaignsPageStats = (campaignStats) => {
  const unwrapped = unwrapDashboardStats(campaignStats);

  return {
    totalCampaigns: pickDashboardStat(unwrapped, ["total", "total_campaigns"]),
    activeCampaigns: pickDashboardStat(unwrapped, ["active", "active_campaigns"]),
  };
};

export const getOrganizationsPageStats = (organizationStats) => {
  const unwrapped = unwrapDashboardStats(organizationStats);

  return {
    totalUsers: pickDashboardStat(unwrapped, ["total_users", "users"]),
    totalInterpreters: pickDashboardStat(unwrapped, [
      "total_interpreters",
      "interpreters",
    ]),
    totalForms: pickDashboardStat(unwrapped, ["total_forms", "forms"]),
    totalCampaigns: pickDashboardStat(unwrapped, [
      "total_campaigns",
      "campaigns",
    ]),
    totalOrganizations: pickDashboardStat(unwrapped, [
      "total_organizations",
      "total_organisations",
      "organizations",
    ]),
  };
};

export const overlayDashboardWithPageStats = (
  dashboardView,
  pageStats = {},
) => {
  const merged = { ...dashboardView };

  Object.entries(pageStats).forEach(([key, value]) => {
    if (value != null && value !== "") {
      merged[key] = value;
    }
  });

  merged.responseRateLabel = `${formatDashboardRate(merged.responseRate)}%`;
  return merged;
};
